/**
 * @file SeoHistoryDialog.tsx
 * @description Окно «Вернуть прежнее» поля общих SEO-настроек (phase19.16,
 * партия Р3в, v0.19.64; SEO16, SB145; контракт r3v-api-contract §5):
 * последние 10 значений за 12 месяцев, у каждого — «было → стало», когда и
 * кто; «Вернуть» ставит полю «было» — действует сразу. Кнопки нет, если
 * прежнее значение совпадает с текущим (`canRevert`).
 *
 * Закрыть сайт от поисковиков возвратом нельзя: это делается только окном
 * со словом «закрыть» в карточке «Индексация» (SB145). Возврат кодов
 * подтверждения предупреждает: кабинет поисковика потеряет подтверждение
 * удалённых кодов (SEO1). Стоп-лист (409 `site_seo_text_blocked`) — ошибки
 * и галочка владельца под строкой (SB125), повтор с `acknowledgeChecks`.
 * До sm — на весь экран; кнопки 48 px.
 */

import { useEffect, useState } from "react";

import { Button } from "../../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../ui/dialog";
import { ListError } from "../../ui/ListStates";
import { useNotify } from "../../ui/NotifyProvider";
import { Skeleton } from "../../ui/skeleton";
import { StopListAck } from "./StopListAck";
import type { ShopSeoApi } from "../../../hooks/site-seo/useShopSeoSettings";
import { shopSeo, type ShopSeoFieldKey, type ShopSeoHistoryItem } from "../../../lib/api-site-seo";
import { formatShopDateTime } from "../../../lib/shop-time";
import { errorCode, seoErrorText, stopListChecks } from "../../../lib/site-seo/seo-errors";
import { historyValueText, SEO_FIELDS } from "../../../lib/site-seo/seo-fields";
import type { PublishCheck } from "../../../lib/site-view/types";

interface Props {
  field: ShopSeoFieldKey | null;
  api: ShopSeoApi;
  onClose: () => void;
}

/** Подпись источника строки журнала. */
const SOURCE_TEXT: Readonly<Record<ShopSeoHistoryItem["source"], string | null>> = {
  EDIT: null,
  REVERT: "возврат",
  LEGACY_DRAFT: "из черновика сайта",
};

const VERIFICATION: ReadonlySet<ShopSeoFieldKey> = new Set(["yandexVerification", "googleVerification"]);

export function SeoHistoryDialog({ field, api, onClose }: Props) {
  const notify = useNotify();
  const [items, setItems] = useState<ShopSeoHistoryItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  // Строка журнала, которую не пустил стоп-лист, и галочка владельца.
  const [blocked, setBlocked] = useState<{ id: string; checks: PublishCheck[] } | null>(null);
  const [ack, setAck] = useState(false);

  useEffect(() => {
    if (!field) return;
    let alive = true;
    setItems(null);
    setError(null);
    setBlocked(null);
    shopSeo
      .history(field)
      .then((list) => alive && setItems(list))
      .catch((err: unknown) => alive && setError(seoErrorText(err, "Не удалось загрузить прежние значения.")));
    return () => {
      alive = false;
    };
  }, [field, attempt]);

  async function revert(item: ShopSeoHistoryItem) {
    setBusy(item.id);
    try {
      await api.revert(item.id, blocked?.id === item.id && ack ? { acknowledgeChecks: true } : {});
      notify.success(`Вернули прежнее значение: ${historyValueText(item.oldValue, item.field)}`);
      onClose();
    } catch (err) {
      const found = stopListChecks(err);
      if (found.length > 0) {
        setBlocked({ id: item.id, checks: found });
        setAck(false);
      } else if (errorCode(err) !== "site_seo_version_conflict") notify.error(seoErrorText(err, "Не удалось вернуть прежнее значение."));
    } finally {
      setBusy(null);
    }
  }

  const label = field ? SEO_FIELDS[field].label : "";
  return (
    <Dialog open={field !== null} onOpenChange={(next) => (!next && !busy ? onClose() : null)}>
      <DialogContent fullScreen className="sm:max-h-[90vh] sm:max-w-[35rem] sm:overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Вернуть прежнее: {label}</DialogTitle>
          <DialogDescription>Последние 10 значений за 12 месяцев. Возврат действует на сайте сразу.</DialogDescription>
        </DialogHeader>
        {field && VERIFICATION.has(field) ? (
          <p className="type-caption text-warning">Если вернуть список без кода, кабинет поисковика потеряет подтверждение при следующей проверке.</p>
        ) : null}
        {error ? (
          <ListError message={error} onRetry={() => setAttempt((n) => n + 1)} />
        ) : items === null ? (
          <div className="flex flex-col gap-2" aria-busy="true" aria-label="Загрузка прежних значений">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-14 rounded-lg" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <p className="type-body text-on-surface-variant">Поле ещё не меняли — возвращать нечего.</p>
        ) : (
          <ul className="flex flex-col divide-y divide-[var(--outline-variant)]">
            {items.map((item) => {
              const closes = item.field === "allowIndexing" && item.oldValue === false;
              const source = SOURCE_TEXT[item.source];
              return (
                <li key={item.id} className="flex flex-col gap-1 py-3">
                  <span className="type-body break-words text-on-surface">
                    было: {historyValueText(item.oldValue, item.field)} → стало: {historyValueText(item.newValue, item.field)}
                  </span>
                  <span className="type-caption text-on-surface-variant">
                    {formatShopDateTime(item.createdAt)}
                    {item.actorName ? ` · ${item.actorName}` : ""}
                    {source ? ` · ${source}` : ""}
                  </span>
                  {closes ? (
                    <span className="type-caption text-on-surface-variant">Закрыть сайт от поисковиков можно только кнопкой в карточке «Индексация».</span>
                  ) : item.canRevert ? (
                    <>
                      {blocked?.id === item.id ? <StopListAck checks={blocked.checks} ack={ack} onAck={setAck} /> : null}
                      <Button
                        variant="outline"
                        className="self-start max-sm:w-full"
                        loading={busy === item.id}
                        disabled={busy !== null || (blocked?.id === item.id && !ack)}
                        onClick={() => void revert(item)}
                      >
                        Вернуть это значение
                      </Button>
                    </>
                  ) : (
                    <span className="type-caption text-on-surface-variant">Это значение действует сейчас.</span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
